import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Chip,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Tabs,
    Tab
} from '@mui/material';
import {
    Upload as UploadIcon,
    Description as DocumentIcon,
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Article as ArticleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { renderAsync } from 'docx-preview';

function PromoComboViewer({
    apiBase = '/api/promo-combo-document',
    title = 'TELUS Promo Combo Price'
}) {
    const { currentUser } = useAuth();
    const [documentInfo, setDocumentInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadDialog, setUploadDialog] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [documentContent, setDocumentContent] = useState(null);
    const [loadingContent, setLoadingContent] = useState(false);
    const [documentSections, setDocumentSections] = useState([]);
    const [activeSection, setActiveSection] = useState(0);
    const fileInputRef = useRef(null);
    const documentViewerRef = useRef(null);

    useEffect(() => {
        fetchDocumentInfo();
    }, []);

    // Render document when content is loaded
    useEffect(() => {
        const renderDocument = async () => {
            if (documentContent && documentViewerRef.current) {
                try {
                    console.log('Rendering document with docx-preview...');
                    console.log('Container element:', documentViewerRef.current);
                    console.log('Container type:', typeof documentViewerRef.current);
                    
                    // Clear the container first
                    if (documentViewerRef.current) {
                        documentViewerRef.current.innerHTML = '';
                        
                        // Try rendering with minimal options first
                        await renderAsync(documentContent, documentViewerRef.current);
                        console.log('Document rendered successfully');
                    } else {
                        console.error('Container ref is null');
                        setError('Failed to get document container');
                        return;
                    }
                    
                    // Add custom styles for better tab rendering
                    const style = document.createElement('style');
                    style.textContent = `
                        .docx-preview-wrapper .docx-wrapper {
                            background: white;
                            padding: 20px;
                        }
                        .docx-preview-wrapper span.tab {
                            display: inline-block;
                            width: 40px;
                        }
                        .docx-preview-wrapper p {
                            white-space: pre-wrap;
                            tab-size: 8;
                        }
                        .docx-preview-wrapper .docx-wrapper section {
                            box-shadow: none !important;
                            padding: 0 !important;
                        }
                    `;
                    document.head.appendChild(style);
                    
                    // Extract sections from the rendered document
                    setTimeout(() => {
                        extractDocumentSections();
                    }, 500); // Wait for rendering to complete
                } catch (renderError) {
                    console.error('Error rendering document:', renderError);
                    setError(`Failed to render document: ${renderError.message}`);
                }
            }
        };
        
        renderDocument();
    }, [documentContent]);

    const fetchDocumentInfo = async () => {
        setLoading(true);
        try {
            console.log('Fetching document info...');
            const response = await fetch(`${apiBase}/info`);
            const data = await response.json();
            console.log('Document info response:', data);
            
            if (data.success) {
                setDocumentInfo(data.document);
                console.log('Document info set:', data.document);
                
                if (data.document && data.document.fileExists) {
                    console.log('File exists, loading content...');
                    loadDocumentContent();
                } else {
                    console.log('File does not exist or no document info');
                }
            } else {
                console.error('Failed to fetch document info:', data.error);
                setError(data.error || 'Failed to fetch document info');
            }
        } catch (err) {
            console.error('Error fetching document info:', err);
            setError('Failed to connect to server');
        }
        setLoading(false);
    };

    const loadDocumentContent = async () => {
        setLoadingContent(true);
        setError(''); // Clear previous errors
        
        try {
            console.log('Loading document content...');
            const response = await fetch(`${apiBase}/view`);
            console.log('Response status:', response.status);
            
            if (response.ok) {
                const arrayBuffer = await response.arrayBuffer();
                console.log('Document loaded, size:', arrayBuffer.byteLength, 'bytes');
                setDocumentContent(arrayBuffer);
            } else {
                console.error('Failed to fetch document, status:', response.status);
                setError('Failed to load document content');
            }
        } catch (err) {
            console.error('Error loading document:', err);
            setError(`Failed to load document: ${err.message}`);
        }
        setLoadingContent(false);
    };


    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                setError('Please select a DOCX file');
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                setError('File size must be less than 10MB');
                return;
            }
            setSelectedFile(file);
            setError('');
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        setError('');
        setSuccess('');

        const formData = new FormData();
        formData.append('document', selectedFile);

        try {
            const response = await fetch(`${apiBase}/upload`, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();
            if (data.success) {
                setSuccess('Document uploaded successfully');
                setUploadDialog(false);
                setSelectedFile(null);
                fetchDocumentInfo();
            } else {
                setError(data.error || 'Upload failed');
            }
        } catch (err) {
            setError('Failed to upload document');
        }
        setUploading(false);
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete the current document?')) {
            return;
        }

        try {
            const response = await fetch(`${apiBase}/delete`, {
                method: 'DELETE'
            });

            const data = await response.json();
            if (data.success) {
                setSuccess('Document deleted successfully');
                setDocumentInfo(null);
                setDocumentContent(null);
            } else {
                setError(data.error || 'Delete failed');
            }
        } catch (err) {
            setError('Failed to delete document');
        }
    };

    const extractDocumentSections = () => {
        if (!documentViewerRef.current) return;
        
        // Look for headings in the rendered document
        const headings = documentViewerRef.current.querySelectorAll('h1, h2, h3, p strong');
        const sections = [];
        
        headings.forEach((heading, index) => {
            let text = heading.textContent.trim();
            
            // Filter out empty or very short headings
            if (text && text.length > 2) {
                // Check if it looks like a section header (often in caps or starts with number)
                const isLikelySection = 
                    text === text.toUpperCase() || // All caps
                    /^\d+\./.test(text) || // Starts with number
                    /^[A-Z][A-Z]/.test(text) || // Starts with multiple caps
                    heading.tagName.toLowerCase().startsWith('h'); // Is actual heading
                
                if (isLikelySection) {
                    // Add unique ID to heading for scrolling
                    heading.id = `section-${index}`;
                    
                    sections.push({
                        id: `section-${index}`,
                        title: text.length > 30 ? text.substring(0, 30) + '...' : text,
                        element: heading
                    });
                }
            }
        });
        
        // If no sections found, try to create sections based on content structure
        if (sections.length === 0) {
            // Look for bold text that might be section headers
            const boldTexts = documentViewerRef.current.querySelectorAll('strong, b');
            boldTexts.forEach((bold, index) => {
                const text = bold.textContent.trim();
                if (text && text.length > 3 && text.length < 50) {
                    bold.id = `section-bold-${index}`;
                    sections.push({
                        id: `section-bold-${index}`,
                        title: text,
                        element: bold
                    });
                }
            });
        }
        
        console.log('Extracted sections:', sections);
        setDocumentSections(sections);
    };
    
    const handleSectionClick = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            
            // Update active section
            const index = documentSections.findIndex(s => s.id === sectionId);
            if (index !== -1) {
                setActiveSection(index);
            }
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading document...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', maxWidth: 'none' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" gutterBottom display="flex" alignItems="center">
                    <DocumentIcon sx={{ mr: 2, color: 'primary.main' }} />
                    {title}
                </Typography>
                <Box display="flex" gap={1}>
                    <Tooltip title="Refresh document">
                        <IconButton onClick={fetchDocumentInfo} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    {currentUser?.isAdmin && (
                        <>
                            <Button
                                variant="contained"
                                startIcon={<UploadIcon />}
                                onClick={() => setUploadDialog(true)}
                                color="primary"
                            >
                                {documentInfo ? 'Update Document' : 'Upload Document'}
                            </Button>
                            {documentInfo && (
                                <Button
                                    variant="outlined"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleDelete}
                                    color="error"
                                >
                                    Delete
                                </Button>
                            )}
                        </>
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {documentInfo ? (
                <Box>
                    {/* Document Info Card */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        {documentInfo.original_name}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                        <Chip 
                                            label={formatFileSize(documentInfo.file_size)}
                                            size="small"
                                            color="info"
                                        />
                                        <Chip 
                                            label="DOCX"
                                            size="small"
                                            color="secondary"
                                        />
                                        <Chip 
                                            label={`Uploaded: ${documentInfo.uploadedAt}`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                    {documentInfo.uploaded_by_name && (
                                        <Typography variant="body2" color="text.secondary">
                                            Uploaded by: {documentInfo.uploaded_by_name}
                                        </Typography>
                                    )}
                                </Box>
                                <DocumentIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Section Navigation Tabs */}
                    {documentSections.length > 0 && (
                        <Paper sx={{ mb: 2 }}>
                            <Tabs
                                value={activeSection}
                                onChange={(e, newValue) => {
                                    setActiveSection(newValue);
                                    handleSectionClick(documentSections[newValue].id);
                                }}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{ borderBottom: 1, borderColor: 'divider' }}
                            >
                                {documentSections.map((section, index) => (
                                    <Tab
                                        key={section.id}
                                        label={section.title}
                                        icon={<ArticleIcon />}
                                        iconPosition="start"
                                        sx={{ minHeight: 48 }}
                                    />
                                ))}
                            </Tabs>
                        </Paper>
                    )}
                    
                    {/* Document Viewer */}
                    <Paper sx={{ p: 3, minHeight: '600px', width: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Document Content
                            {documentSections.length > 0 && (
                                <Chip 
                                    label={`${documentSections.length} sections found`}
                                    size="small"
                                    color="info"
                                    sx={{ ml: 2 }}
                                />
                            )}
                        </Typography>
                        
                        {loadingContent ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
                                <CircularProgress />
                                <Typography sx={{ ml: 2 }}>Loading document content...</Typography>
                            </Box>
                        ) : documentContent ? (
                            <Box 
                                ref={documentViewerRef}
                                sx={{ 
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    minHeight: '500px',
                                    p: 1,
                                    backgroundColor: '#fff',
                                    overflow: 'auto',
                                    width: '100%',
                                    '& .docx-wrapper': {
                                        width: '100% !important',
                                        maxWidth: 'none !important',
                                        margin: '0 !important'
                                    },
                                    '& .docx-wrapper > section': {
                                        width: '100% !important',
                                        maxWidth: 'none !important'
                                    }
                                }}
                            >
                                {/* Document will be rendered here by docx-preview */}
                            </Box>
                        ) : (
                            <Alert severity="warning">
                                Document content could not be loaded. Please try refreshing the page.
                            </Alert>
                        )}
                    </Paper>
                </Box>
            ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <DocumentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No document uploaded yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        {currentUser?.isAdmin 
                            ? 'Upload a DOCX document to get started.'
                            : 'No pricing document is currently available. Please contact your administrator.'
                        }
                    </Typography>
                    {currentUser?.isAdmin && (
                        <Button
                            variant="contained"
                            startIcon={<UploadIcon />}
                            onClick={() => setUploadDialog(true)}
                            color="primary"
                            size="large"
                        >
                            Upload Document
                        </Button>
                    )}
                </Paper>
            )}

            {/* Upload Dialog */}
            <Dialog 
                open={uploadDialog} 
                onClose={() => !uploading && setUploadDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {documentInfo ? 'Update Document' : 'Upload Document'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".docx"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        
                        <Paper 
                            sx={{ 
                                p: 3, 
                                textAlign: 'center', 
                                border: '2px dashed #ccc',
                                cursor: 'pointer',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    backgroundColor: 'action.hover'
                                }
                            }}
                            onClick={() => fileInputRef.current.click()}
                        >
                            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                            <Typography variant="h6" gutterBottom>
                                {selectedFile ? selectedFile.name : 'Click to select DOCX file'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Maximum file size: 10MB • Only DOCX files allowed
                            </Typography>
                            {selectedFile && (
                                <Chip 
                                    label={formatFileSize(selectedFile.size)}
                                    color="primary"
                                    sx={{ mt: 1 }}
                                />
                            )}
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setUploadDialog(false)}
                        disabled={uploading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUpload}
                        variant="contained"
                        disabled={!selectedFile || uploading}
                        startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                    >
                        {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default PromoComboViewer;
